/**
 * ProgressDialog.tsx - Progress Dialog Component
 * 
 * CHANGE HISTORY:
 * - 251025_1735 danielbeer: Migrated from Promptr with enhancements
 * 
 * Shows progress for long-running operations with determinate/indeterminate modes
 */

import React, { useEffect, useState } from 'react';
import './ProgressDialog.css';

/**
 * Progress Dialog Configuration
 */
export interface ProgressDialogConfig {
  /** Dialog title */
  title?: string;
  /** Progress message */
  message?: string;
  /** Progress value (0-100) */
  progress?: number;
  /** Indeterminate progress (no percentage) */
  indeterminate?: boolean;
  /** Can be canceled */
  cancelable?: boolean;
  /** Cancel callback */
  onCancel?: () => void;
  /** Show percentage */
  showPercentage?: boolean;
  /** Show time remaining */
  showTimeRemaining?: boolean;
  /** Time remaining in seconds */
  timeRemaining?: number | null;
  /** Sub-task name */
  subTask?: string | null;
  /** Sub-task progress (0-100) */
  subProgress?: number | null;
  /** Dialog is open */
  isOpen?: boolean;
  /** Close callback */
  onClose?: () => void;
}

/**
 * Format time in seconds to human-readable string
 */
const formatTime = (seconds: number | null): string => {
  if (!seconds || seconds < 0) return '';
  
  if (seconds < 60) {
    return `${Math.round(seconds)}s remaining`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  
  if (minutes < 60) {
    return `${minutes}m ${secs}s remaining`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m remaining`;
};

/**
 * Progress Dialog Component
 * 
 * Features:
 * - Determinate (0-100%) or indeterminate progress
 * - Status message updates
 * - Cancelable operations
 * - Estimated time remaining
 * - Sub-task progress
 * 
 * @example
 * ```tsx
 * <ProgressDialog
 *   title="Uploading Files"
 *   message="Please wait..."
 *   progress={45}
 *   showPercentage
 *   showTimeRemaining
 *   timeRemaining={120}
 *   cancelable
 *   onCancel={() => console.log('Canceled')}
 * />
 * ```
 */
export const ProgressDialog: React.FC<ProgressDialogConfig> = ({
  title = 'Processing...',
  message = '',
  progress = 0,
  indeterminate = false,
  cancelable = false,
  onCancel,
  showPercentage = true,
  showTimeRemaining = false,
  timeRemaining = null,
  subTask = null,
  subProgress = null,
  isOpen = true,
  onClose
}) => {
  const [startTime] = useState(Date.now());
  const [estimatedTime, setEstimatedTime] = useState<number | null>(timeRemaining);

  // Calculate estimated time remaining based on progress
  useEffect(() => {
    if (!indeterminate && progress > 0 && progress < 100 && !timeRemaining) {
      const elapsed = (Date.now() - startTime) / 1000; // seconds
      const rate = progress / elapsed; // percent per second
      const remaining = (100 - progress) / rate;
      setEstimatedTime(remaining);
    } else if (timeRemaining !== null) {
      setEstimatedTime(timeRemaining);
    }
  }, [progress, indeterminate, timeRemaining, startTime]);

  if (!isOpen) return null;

  const handleCancel = () => {
    if (cancelable && onCancel) {
      onCancel();
    }
  };

  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <div className="progress-dialog-overlay">
      <div className="progress-dialog">
        <div className="progress-dialog-header">
          <h3>{title}</h3>
        </div>

        <div className="progress-dialog-body">
          {message && (
            <p className="progress-message">{message}</p>
          )}

          <div className="progress-bar-container">
            <div className="progress-bar-track">
              <div 
                className={`progress-bar-fill ${indeterminate ? 'indeterminate' : ''}`}
                style={{ 
                  width: indeterminate ? '100%' : `${clampedProgress}%` 
                }}
              />
            </div>
            
            {showPercentage && !indeterminate && (
              <div className="progress-percentage">
                {Math.round(clampedProgress)}%
              </div>
            )}
          </div>

          {showTimeRemaining && estimatedTime !== null && !indeterminate && (
            <div className="progress-time-remaining">
              {formatTime(estimatedTime)}
            </div>
          )}

          {subTask && (
            <div className="progress-subtask">
              <div className="progress-subtask-name">{subTask}</div>
              {subProgress !== null && (
                <div className="progress-subtask-bar">
                  <div className="progress-bar-track">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${Math.min(100, Math.max(0, subProgress))}%` }}
                    />
                  </div>
                  <div className="progress-subtask-percentage">
                    {Math.round(subProgress)}%
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {cancelable && (
          <div className="progress-dialog-footer">
            <button 
              className="progress-cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressDialog;
